import './style.css';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useToast, Dialog } from '@economic/taco';
import { useCurrentAgreement } from '@economic/react-app';
import { ProgressDots } from '~/app/shared/components/ui/progressDots/ProgressDots';
import LoadingBox from '../../pages/home/PlanUpgradeDialog/LoadingBox';
import { useLocalization, t as t2 } from '../../shared/components/l10n/l10n';
import useCachedQuery from '../../shared/hooks/useCachedQuery';
import StepAccountingYear from './StepAccountingYear';
import StepBankInfo from './StepBankInfo';
import StepBusinessInfo from './StepBusinessInfo';
import StepCompanyType from './StepCompanyType';
import StepSuccess from './StepSuccess';
import StepWelcome from './StepWelcome';
import { OnboardingStatus } from './types';
import StepEInvoice from './StepEInvoice';
import StepRackbeat from './StepRackbeat';
import { getCompanyTypes } from './GetCompanyTypes';

interface Props {
    onDone: () => void;
}

function GettingStartedDialog({ onDone }: Props) {
    enum MandatorySteps {
        BUSINESS_INFO = 1,
        COMPANY_TYPE = 2,
        ACCOUNTING_YEAR = 3,
        BANK_INFO = 4,
        EINVOICING = 5,
    }
    const numberOfMandatorySteps = Object.values(MandatorySteps).filter(key => isNaN(Number(key))).length;
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [show, setShow] = useState(true);
    const [rackbeatStepNumber, setRackbeatStepNumber] = useState(-1);
    const [totalNumberOfSteps, setTotalNumberOfSteps] = useState(numberOfMandatorySteps);
    const [companyType, setCompanyType] = useState<string[]>();
    const [isInterestedInRackbeat, setIsInterestedInRackbeat] = React.useState<boolean | undefined>(undefined);

    const { loading, data, refetch } = useCachedQuery<OnboardingStatus>('onboarding');
    const loadingTexts = useLocalization(textIds);
    const toast = useToast();
    const companyTypes = getCompanyTypes();

    // number of steps doesn't include start and end screens
    function isRackbeatStepVisible(cType: string | string[] | undefined) {
        return companyTypes.some(c => c.isRackbeatStepVisible && cType !== undefined && cType.indexOf(c.code) >= 0);
    }

    function onCompanyTypeChange(selectedCompanyTypes) {
        const showRackbeatStep = isRackbeatStepVisible(selectedCompanyTypes);
        if (showRackbeatStep) {
            if (rackbeatStepNumber < 0) {
                setRackbeatStepNumber(totalNumberOfSteps + 1);
                setTotalNumberOfSteps(totalNumberOfSteps + 1);
            }
        } else if (rackbeatStepNumber > 0) {
            setRackbeatStepNumber(-1);
            setTotalNumberOfSteps(totalNumberOfSteps - 1);
        }

        setCompanyType(selectedCompanyTypes);
    }

    useEffect(() => {
        if (step > 0) return;

        // Account activation information is shown in the dialog body rather than in toast.
        const messageShownInDialog = !loading && data && data.accountActivationLinkSent;

        if (!loadingTexts && !loading && !messageShownInDialog) {
            toast.success(t2`TOAST_GETTING_STARTED_COMPLETE_SUCCESS`);
        }
    }, [loadingTexts, loading, step, data, toast]);

    if (loadingTexts) {
        return null;
    }

    function back() {
        if (step > 1) setStep(step - 1);
    }

    async function reloadAndNext() {
        await refetch();
        next();
    }
    function next() {
        setStep(step + 1);
    }

    function close() {
        setShow(false);
        window.top?.refreshRouting();
        window.location.pathname = '/';
        onDone();
    }

    return (
        <Dialog open={show} showCloseButton={false} closeOnEscape={false} size={step === 0 ? 'xs' : 'sm'}>
            <Dialog.Content aria-label="">
                {loading && !data ? (
                    <LoadingBox />
                ) : (
                    <>
                        {step === 0 && (
                            <StepWelcome displaySubcontent={data.accountActivationLinkSent} onGettingStarted={() => setStep(1)} />
                        )}
                        {step === MandatorySteps.BUSINESS_INFO && (
                            <StepBusinessInfo
                                companyData={data.companyData}
                                companyTypes={companyTypes}
                                onDone={reloadAndNext}
                                companyType={companyType}
                                setCompanyType={value => onCompanyTypeChange(value)}>
                                <ProgressDots stepsCount={totalNumberOfSteps} currentStep={MandatorySteps.BUSINESS_INFO} />
                            </StepBusinessInfo>
                        )}
                        {step === MandatorySteps.COMPANY_TYPE && (
                            <StepCompanyType setups={data.setups} onBack={back} onDone={reloadAndNext} onSkip={next}>
                                <ProgressDots stepsCount={totalNumberOfSteps} currentStep={MandatorySteps.COMPANY_TYPE} />
                            </StepCompanyType>
                        )}
                        {step === MandatorySteps.ACCOUNTING_YEAR && (
                            <StepAccountingYear data={data.accountingYear} onBack={back} onDone={reloadAndNext}>
                                <ProgressDots stepsCount={totalNumberOfSteps} currentStep={MandatorySteps.ACCOUNTING_YEAR} />
                            </StepAccountingYear>
                        )}
                        {step === MandatorySteps.BANK_INFO && (
                            <StepBankInfo company={data.companyData} onBack={back} onDone={reloadAndNext} onSkip={next}>
                                <ProgressDots stepsCount={totalNumberOfSteps} currentStep={MandatorySteps.BANK_INFO} />
                            </StepBankInfo>
                        )}
                        {step === MandatorySteps.EINVOICING && (
                            <StepEInvoice company={data.companyData} onBack={back} onDone={reloadAndNext} onSkip={next}>
                                <ProgressDots stepsCount={totalNumberOfSteps} currentStep={MandatorySteps.EINVOICING} />
                            </StepEInvoice>
                        )}
                        {step === rackbeatStepNumber && isRackbeatStepVisible(companyType) && (
                            <StepRackbeat
                                setIsInterestedInRackbeat={value => setIsInterestedInRackbeat(value)}
                                isInterestedInRackbeat={isInterestedInRackbeat}
                                onDone={() => {
                                    if (!isInterestedInRackbeat) {
                                        window.open('https://www.e-conomic.dk/funktioner/lager-af-rackbeat#kontakt', '_blank');
                                    }
                                    next();
                                }}
                                onSkip={next}>
                                <ProgressDots stepsCount={totalNumberOfSteps} currentStep={rackbeatStepNumber} />
                            </StepRackbeat>
                        )}
                        {step === totalNumberOfSteps + 1 && (
                            <StepSuccess
                                onDone={() => {
                                    if (isInterestedInRackbeat) {
                                        setShow(false);
                                        onDone();
                                        navigate('/market/addon/rackbeatlager');
                                    } else {
                                        close();
                                    }
                                }}
                                isRackbeatStepVisible={isRackbeatStepVisible(companyType)}
                                isInterestedInRackbeat={isInterestedInRackbeat}
                            />
                        )}
                    </>
                )}
            </Dialog.Content>
        </Dialog>
    );
}

function withGettingStarted(Component: React.ComponentType<{ onDone: () => void }>) {
    return (props: any) => {
        const agreement = useCurrentAgreement();

        if (!agreement.showGettingStarted) {
            return null;
        }

        return <Component {...props} />;
    };
}

export default withGettingStarted(GettingStartedDialog);

const textIds = [
    'ONBOARDING_GETTINGSTARTED_WELCOME_TITLE',
    'ONBOARDING_GETTINGSTARTED_WELCOME_CONTENT',
    'ONBOARDING_GETTINGSTARTED_WELCOME_SUBCONTENT',
    'ONBOARDING_GETTINGSTARTED_WELCOME_CTA',
    'ONBOARDING_GETTINGSTARTED_TOAST_TITLE',
    'TOAST_GETTING_STARTED_COMPLETE_SUCCESS',

    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_TITLE',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_CONTENT',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_CVR',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_CONTACTPERSON',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_NAME',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_EMAIL',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_ZIPCODE',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_CITY',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_PHONE',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_ADDRESS',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_WEBSITE',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_VALIDATION_REQUIRED_MESSAGE',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_EMAIL_VALIDATION_MESSAGE',
    'ONBOARDING_GETTINGSTARTED_BUSINESSINFO_PHONE_VALIDATION_MESSAGE',

    'ONBOARDING_GETTINGSTARTED_COMPANYTYPE_TITLE',
    'ONBOARDING_GETTINGSTARTED_COMPANYTYPE_CONTENT',
    'ONBOARDING_GETTINGSTARTED_COMPANYTYPE_MUSTCHOOSESETUP',
    'ONBOARDING_GETTINGSTARTED_COMPANYTYPE_SEEMORE',

    'ONBOARDING_GETTINGSTARTED_ACCOUNTINGYEAR_TITLE',
    'ONBOARDING_GETTINGSTARTED_ACCOUNTINGYEAR_CONTENT',

    'ONBOARDING_GETTINGSTARTED_BANKINFO_TITLE',
    'ONBOARDING_GETTINGSTARTED_BANKINFO_CONTENT',
    'ONBOARDING_GETTINGSTARTED_BANKINFO_NAME',
    'ONBOARDING_GETTINGSTARTED_BANKINFO_ACCOUNT',
    'ONBOARDING_GETTINGSTARTED_BANKINFO_NUMBER',
    'ONBOARDING_GETTINGSTARTED_BANKINFO_SWIFT',
    'ONBOARDING_GETTINGSTARTED_BANKINFO_IBAN',
    'ONBOARDING_GETTINGSTARTED_BANKINFO_NAME_REQUIRED',

    'ONBOARDING_GETTINGSTARTED_SUCCESS_TITLE',
    'ONBOARDING_GETTINGSTARTED_SUCCESS_CONTENT',
    'ONBOARDING_GETTINGSTARTED_SUCCESS_CTA',
    'ONBOARDING_GETTINGSTARTED_SUCCESS_TIP1',
    'ONBOARDING_GETTINGSTARTED_SUCCESS_TIP2',
    'ONBOARDING_GETTINGSTARTED_SUCCESS_TIP3',

    'ONBOARDING_GETTINGSTARTED_BACK',
    'ONBOARDING_GETTINGSTARTED_NEXT',
    'ONBOARDING_GETTINGSTARTED_SKIP',

    // Old, from gettingStarted.aspx and reused here
    'GETTING_STARTED_ACCOUNTING_YEAR_OPTION_CURRENT',
    'GETTING_STARTED_ACCOUNTING_YEAR_OPTION_SHIFTED',
    'GETTING_STARTED_ACCOUNTING_YEAR_OPTION_CUSTOM',
    'GETTING_STARTED_ACCOUNTYEARS_ALREADY_CREATED',
    'GETTING_STARTED_ACCOUNTYEARS_ALREADY_CREATED_HINT_CHANGE_I',
    'GETTING_STARTED_ACCOUNTYEARS_ALREADY_CREATED_HINT_CHANGE_II_PART_I',
    'GETTING_STARTED_ACCOUNTYEARS_ALREADY_CREATED_HINT_CHANGE_II_PART_II',
    'GETTING_STARTED_ACCOUNTYEARS_ALREADY_CREATED_HINT_CHANGE_II_PART_III',
    'GETTING_STARTED_DEFAULT_SETUP_NAME_ALREADY_APPLIED',
    'GETTING_STARTED_DEFAULT_SETUP_NAME_ALREADY_APPLIED_HINT_CHANGE_I',
    'GETTING_STARTED_DEFAULT_SETUP_NAME_ALREADY_APPLIED_HINT_CHANGE_II_PART_I',
    'GETTING_STARTED_DEFAULT_SETUP_NAME_ALREADY_APPLIED_HINT_CHANGE_II_PART_II',
    'GETTING_STARTED_DEFAULT_SETUP_NAME_ALREADY_APPLIED_HINT_CHANGE_II_PART_III',
    'GETTING_STARTED_CANNOT_APPLY_DEFAULT_SETUP_REASON',
    'GETTING_STARTED_DEFAULT_SETUP_CANNOT_APPLY_MORE_INFO',
    // economic go
    'ECONOMIC_GO_YES',
    'ECONOMIC_GO_NO',
];
